/**
 * Shared marketing-site footer — dark cinema chrome used across every
 * public route. Matches the visual idiom of the legacy inline footer in
 * app/page.tsx (VelovaLogo, velova-mono-label column headers, outlined
 * wordmark) but is CMS-agnostic: a later task imports this into every
 * marketing route, including a rebuilt app/page.tsx.
 */

import Link from "next/link"
import { VelovaLogo } from "@/components/velova-logo"
import { SITE_META } from "@/lib/site-config"

interface FooterLink {
  label: string
  href: string
}

interface FooterColumn {
  heading: string
  links: FooterLink[]
}

const COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "How it works", href: "/how-it-works" },
      { label: "Pricing", href: "/pricing" },
      { label: "AI models", href: "/ai-models" },
    ],
  },
  {
    heading: "Solutions",
    links: [
      { label: "Marketing teams", href: "/for-marketing-teams" },
      { label: "Agencies", href: "/agencies" },
      { label: "Industry guides", href: "/learn" },
    ],
  },
  {
    heading: "Learn",
    links: [
      { label: "What is GEO?", href: "/what-is-geo" },
      { label: "GEO vs SEO", href: "/geo-vs-seo" },
      { label: "Glossary", href: "/geo-glossary" },
      { label: "Learn hub", href: "/learn" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    heading: "Access",
    links: [
      { label: "Start free", href: "/signup" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
    ],
  },
]

export function SiteFooter() {
  return (
    <footer className="bg-[#000000] text-[#f4f1ea] relative overflow-hidden border-t border-white/10">
      <div className="max-w-none mx-auto px-4 sm:px-8 lg:px-12 pt-16 sm:pt-20 pb-10">
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <VelovaLogo variant="light" width={160} />
          </div>
          <p className="text-[15px] sm:text-[17px] leading-[1.6] text-white/60 max-w-xl">
            {SITE_META.footerPositioning}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-8 mb-20">
          {COLUMNS.map((column) => (
            <div key={column.heading}>
              <div className="velova-mono-label opacity-50 mb-4">{column.heading}</div>
              <ul className="space-y-2 text-[14px]">
                {column.links.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href} className="hover:opacity-60 transition-opacity">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-[rgba(255,255,255,0.12)] pt-8">
          <div className="text-[12px] opacity-60">{SITE_META.copyright}</div>
        </div>
      </div>

      {/* Subtle wordmark at bottom */}
      <div className="relative select-none pointer-events-none pb-6">
        <div
          className="velova-display leading-[0.85] text-center whitespace-nowrap overflow-hidden opacity-90"
          style={{
            fontSize: "clamp(60px, 10vw, 132px)",
            letterSpacing: "-0.02em",
            color: "transparent",
            WebkitTextStroke: "1px rgba(247, 245, 239, 0.14)",
          }}
        >
          VELOVA
        </div>
      </div>
    </footer>
  )
}
